import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Menu, Send, Mic, MicOff, Volume2, Sparkles } from 'lucide-react';
import api, { getErrorMessage } from '../lib/api';
import { cn } from '../lib/utils';
import { useToast } from '../components/ui/Toast';
import { useChatSocket } from '../hooks/useChatSocket';
import { useSpeechRecognition, useTextToSpeech } from '../hooks/useSpeech';
import { useAuth } from '../context/AuthContext';
import ConversationSidebar from '../components/chat/ConversationSidebar';
import MessageBubble from '../components/chat/MessageBubble';
import { LogoMark } from '../components/ui/Logo';

const SUGGESTIONS = [
  'Help me craft a prompt for a cyberpunk city image',
  'Explain async/await like I am five',
  'Write a haiku about the ocean',
  'Ideas for a lo-fi track to study to',
];

const uid = () => Math.random().toString(36).slice(2);

export default function Chat() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [input, setInput] = useState('');
  const [autoSpeak, setAutoSpeak] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);

  const streamIdRef = useRef(null);
  const bottomRef = useRef(null);
  const baseTranscript = useRef('');

  const tts = useTextToSpeech();

  const { data: convData, isLoading: convLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: async () => (await api.get('/chat/conversations')).data,
  });
  const conversations = convData?.conversations || [];

  // ---- WebSocket streaming ----
  const { status, sendChat } = useChatSocket({
    onConversation: (conv) => {
      setActiveId(conv.id);
    },
    onToken: (delta) => {
      setMessages((m) =>
        m.map((msg) => (msg.id === streamIdRef.current ? { ...msg, content: msg.content + delta } : msg))
      );
    },
    onDone: (message) => {
      setMessages((m) =>
        m.map((msg) =>
          msg.id === streamIdRef.current ? { ...msg, id: message.id, content: message.content } : msg
        )
      );
      setStreaming(false);
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      if (autoSpeak) tts.speak(message.content);
    },
    onError: (err) => {
      setStreaming(false);
      // Drop the empty placeholder bubble.
      setMessages((m) => m.filter((msg) => !(msg.id === streamIdRef.current && !msg.content)));
      if (err.code === 'USAGE_LIMIT') {
        toast.error(err.message, {
          title: 'Plan limit reached',
          duration: Infinity,
          action: { label: 'Upgrade', onClick: () => navigate('/app/pricing') },
        });
      } else {
        toast.error(err.message || 'The assistant hit an error.');
      }
    },
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ---- Voice input ----
  const speech = useSpeechRecognition({
    onResult: (transcript) => {
      setInput((baseTranscript.current ? baseTranscript.current + ' ' : '') + transcript);
    },
  });
  const onMic = () => {
    if (!speech.listening) baseTranscript.current = input;
    speech.toggle();
  };

  // ---- Actions ----
  const selectConversation = async (id) => {
    setDrawerOpen(false);
    if (id === activeId) return;
    setActiveId(id);
    setMessages([]);
    try {
      const { data } = await api.get(`/chat/conversations/${id}`);
      setMessages(data.conversation.messages);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load conversation.'));
    }
  };

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
    setDrawerOpen(false);
  };

  const send = (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    if (status !== 'open') {
      toast.warning('Reconnecting to the assistant — try again in a moment.');
      return;
    }
    const streamId = `a-${uid()}`;
    streamIdRef.current = streamId;
    setMessages((m) => [
      ...m,
      { id: `u-${uid()}`, role: 'USER', content },
      { id: streamId, role: 'ASSISTANT', content: '' },
    ]);
    setStreaming(true);
    setInput('');
    baseTranscript.current = '';
    if (speech.listening) speech.toggle();
    sendChat({ conversationId: activeId, content });
  };

  const togglePin = async (c) => {
    qc.setQueryData(['conversations'], (old) => ({
      conversations: old.conversations.map((x) => (x.id === c.id ? { ...x, pinned: !x.pinned } : x)),
    }));
    try {
      await api.patch(`/chat/conversations/${c.id}`, { pinned: !c.pinned });
    } finally {
      qc.invalidateQueries({ queryKey: ['conversations'] });
    }
  };

  const deleteConversation = async (c) => {
    qc.setQueryData(['conversations'], (old) => ({
      conversations: old.conversations.filter((x) => x.id !== c.id),
    }));
    if (c.id === activeId) newChat();
    try {
      await api.delete(`/chat/conversations/${c.id}`);
    } catch {
      qc.invalidateQueries({ queryKey: ['conversations'] });
    }
  };

  const speakMessage = (message) => {
    if (speakingId === message.id && tts.speaking) {
      tts.cancel();
      setSpeakingId(null);
    } else {
      tts.speak(message.content);
      setSpeakingId(message.id);
    }
  };

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-4 md:h-[calc(100vh-5rem)]">
      <ConversationSidebar
        conversations={conversations}
        activeId={activeId}
        loading={convLoading}
        onSelect={selectConversation}
        onNew={newChat}
        onPin={togglePin}
        onDelete={deleteConversation}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      {/* Chat column */}
      <div className="flex min-w-0 flex-1 flex-col rounded-2xl border border-border/60 bg-surface/30">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDrawerOpen(true)}
              className="rounded-lg p-2 text-content-subtle hover:bg-white/5 hover:text-content md:hidden"
              aria-label="Conversations"
            >
              <Menu className="h-5 w-5" />
            </button>
            <LogoMark className="h-6 w-6" />
            <div>
              <p className="text-sm font-semibold leading-tight">Aether Assistant</p>
              <p className="flex items-center gap-1.5 text-xs text-content-subtle">
                <span
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    status === 'open' ? 'bg-success' : 'bg-warning'
                  )}
                />
                {status === 'open' ? 'Connected' : 'Reconnecting…'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setAutoSpeak((a) => !a)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs transition-colors',
              autoSpeak ? 'bg-brand-500/15 text-brand-300' : 'text-content-subtle hover:bg-white/5'
            )}
            title="Auto-read replies aloud"
          >
            <Volume2 className="h-4 w-4" /> Auto-speak {autoSpeak ? 'on' : 'off'}
          </button>
        </div>

        {/* Messages */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5 no-scrollbar">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <div className="rounded-2xl bg-brand-500/10 p-4 glow-pulse">
                <Sparkles className="h-8 w-8 text-brand-300" />
              </div>
              <div>
                <p className="font-display text-xl font-semibold">How can I help you create?</p>
                <p className="mt-1 text-sm text-content-muted">
                  Ask anything, or get help crafting prompts for the studios.
                </p>
              </div>
              <div className="mt-2 grid max-w-lg grid-cols-1 gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="glass rounded-xl px-4 py-3 text-left text-sm text-content-muted transition-colors hover:text-content"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => (
              <MessageBubble
                key={m.id}
                message={m}
                user={user}
                onSpeak={speakMessage}
                speaking={speakingId === m.id && tts.speaking}
                isStreaming={streaming && m.id === streamIdRef.current}
              />
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        <div className="border-t border-border/60 p-3">
          <div className="glass flex items-end gap-2 rounded-xl p-2">
            {speech.supported && (
              <button
                onClick={onMic}
                className={cn(
                  'shrink-0 rounded-lg p-2.5 transition-colors',
                  speech.listening ? 'bg-danger/15 text-danger' : 'text-content-subtle hover:bg-white/5'
                )}
                aria-label={speech.listening ? 'Stop voice input' : 'Start voice input'}
              >
                {speech.listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </button>
            )}
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder="Message Aether…  (Enter to send, Shift+Enter for newline)"
              className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent px-1 py-2 text-sm text-content placeholder:text-content-subtle focus:outline-none"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || streaming}
              className="shrink-0 rounded-lg bg-brand-500 p-2.5 text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
